/**
 * Generic IndexedDB wrapper.
 *
 * Thin typed surface over the existing `idb` dependency. Two design choices
 * worth knowing:
 *
 *   1. Each operation opens its own connection and closes it on completion.
 *      IndexedDB does not require a long-lived connection, and short-lived
 *      ones avoid the classic "tab A holds an open DB at v1 while tab B
 *      tries to upgrade to v2" deadlock.
 *
 *   2. The schema callback runs only inside `openDb()`. Stores not present
 *      on `openDb` are not created — the caller must declare every store
 *      it intends to touch when it opens the DB. This makes schema drift
 *      a compile-time-visible event rather than a silent runtime quietness.
 *
 * Vue parallel: `RW-ERP-Service/src/composables/mastery/mastery-store-db.ts`
 * implements the same primitives by hand on top of raw `indexedDB`. We
 * lean on `idb` for the promise wrapping and stay closer to its primitives.
 */

import { openDB as openIdb, IDBPDatabase, IDBPTransaction } from 'idb';

import { logger } from '../utils/logger';

export interface ObjectStoreSchema {
  /** Object store name. */
  name: string;
  /** Key path. Defaults to `'id'` (matches Vue convention). */
  keyPath?: string;
  /** Whether the store auto-increments. Defaults to false. */
  autoIncrement?: boolean;
}

export interface OpenDbOptions {
  database: string;
  version: number;
  stores: ReadonlyArray<ObjectStoreSchema>;
}

const isIdbAvailable = (): boolean => {
  try {
    return typeof indexedDB !== 'undefined';
  } catch {
    return false;
  }
};

/**
 * Opens a database, creating any stores in `options.stores` that don't
 * already exist. The returned connection should be closed by the caller
 * (or use the higher-level helpers below which handle lifecycle).
 */
export async function openDb(options: OpenDbOptions): Promise<IDBPDatabase | null> {
  if (!isIdbAvailable()) {
    logger.warn('[indexedDb] IndexedDB not available in this environment');
    return null;
  }

  try {
    return await openIdb(options.database, options.version, {
      upgrade(db) {
        for (const store of options.stores) {
          if (!db.objectStoreNames.contains(store.name)) {
            db.createObjectStore(store.name, {
              keyPath: store.keyPath ?? 'id',
              autoIncrement: store.autoIncrement ?? false,
            });
          }
        }
      },
      blocked() {
        logger.warn('[indexedDb] open blocked — another tab holds an older version');
      },
    });
  } catch (err) {
    logger.warn('[indexedDb] open failed', { database: options.database, err });
    return null;
  }
}

/**
 * Read one record by key. Returns `undefined` when missing OR when IDB is
 * unavailable / failed — callers should treat that as a cache miss and fall
 * back to the network.
 */
export async function get<T = unknown>(
  options: OpenDbOptions,
  storeName: string,
  key: IDBValidKey,
): Promise<T | undefined> {
  const db = await openDb(options);
  if (!db) return undefined;
  try {
    return (await db.get(storeName, key)) as T | undefined;
  } catch (err) {
    logger.warn('[indexedDb] get failed', { storeName, key, err });
    return undefined;
  } finally {
    db.close();
  }
}

/**
 * Read every record from a store. Order is unspecified — callers that need
 * deterministic ordering should sort the result themselves.
 */
export async function getAll<T = unknown>(
  options: OpenDbOptions,
  storeName: string,
): Promise<ReadonlyArray<T>> {
  const db = await openDb(options);
  if (!db) return [];
  try {
    return (await db.getAll(storeName)) as T[];
  } catch (err) {
    logger.warn('[indexedDb] getAll failed', { storeName, err });
    return [];
  } finally {
    db.close();
  }
}

/**
 * Read every KEY in a store, without deserialising the records behind them.
 *
 * The distinction from `getAll` matters when records are large: a cache that
 * needs to know what it holds — to enforce a size cap, or to find a key that
 * supersedes the one being asked for — should not have to pull megabytes of
 * payload through `structuredClone` to answer. Keys that encode their own
 * metadata (`kind|user|params`) can be inspected on their own.
 *
 * Returns `[]` when IDB is unavailable or the read fails, so a caller treats it
 * as "nothing cached" rather than having to handle an error.
 */
export async function getAllKeys(
  options: OpenDbOptions,
  storeName: string,
): Promise<ReadonlyArray<IDBValidKey>> {
  const db = await openDb(options);
  if (!db) return [];
  try {
    return await db.getAllKeys(storeName);
  } catch (err) {
    logger.warn('[indexedDb] getAllKeys failed', { storeName, err });
    return [];
  } finally {
    db.close();
  }
}

/**
 * Write or overwrite a single record. The record must include the configured
 * keyPath (default `'id'`).
 */
export async function put<T extends object>(
  options: OpenDbOptions,
  storeName: string,
  record: T,
): Promise<boolean> {
  const db = await openDb(options);
  if (!db) return false;
  try {
    await db.put(storeName, record);
    return true;
  } catch (err) {
    logger.warn('[indexedDb] put failed', { storeName, err });
    return false;
  } finally {
    db.close();
  }
}

/**
 * Write multiple records in a single transaction. Either all succeed or
 * none persist — the transaction aborts on the first failure.
 */
export async function putMany<T extends object>(
  options: OpenDbOptions,
  storeName: string,
  records: ReadonlyArray<T>,
): Promise<boolean> {
  if (records.length === 0) return true;
  const db = await openDb(options);
  if (!db) return false;
  try {
    const tx: IDBPTransaction<unknown, [string], 'readwrite'> = db.transaction(
      storeName,
      'readwrite',
    );
    await Promise.all(records.map((r) => tx.store.put(r)));
    await tx.done;
    return true;
  } catch (err) {
    logger.warn('[indexedDb] putMany failed', { storeName, err });
    return false;
  } finally {
    db.close();
  }
}

export async function del(
  options: OpenDbOptions,
  storeName: string,
  key: IDBValidKey,
): Promise<boolean> {
  const db = await openDb(options);
  if (!db) return false;
  try {
    await db.delete(storeName, key);
    return true;
  } catch (err) {
    logger.warn('[indexedDb] del failed', { storeName, key, err });
    return false;
  } finally {
    db.close();
  }
}

export async function clearStore(
  options: OpenDbOptions,
  storeName: string,
): Promise<boolean> {
  const db = await openDb(options);
  if (!db) return false;
  try {
    await db.clear(storeName);
    return true;
  } catch (err) {
    logger.warn('[indexedDb] clearStore failed', { storeName, err });
    return false;
  } finally {
    db.close();
  }
}

/**
 * Delete every IndexedDB on the current origin. Used on logout to mirror
 * Vue's `clearAllIndexedDB()` in `composables/mastery/mastery-store-db.ts`.
 *
 * `indexedDB.databases()` is widely supported but is not in the standard
 * type lib for every TS version. The cast keeps this compatible with the
 * project's TS 4.9 baseline without pulling in extra lib dependencies.
 */
export async function deleteAllDatabases(): Promise<void> {
  if (!isIdbAvailable()) return;
  const enumerator = (
    indexedDB as unknown as {
      databases?: () => Promise<Array<{ name?: string }>>;
    }
  ).databases;
  if (!enumerator) {
    logger.warn('[indexedDb] indexedDB.databases() unsupported in this browser');
    return;
  }
  try {
    const dbs = await enumerator.call(indexedDB);
    await Promise.all(
      dbs
        .map((d) => d.name)
        .filter((name): name is string => Boolean(name))
        .map(
          (name) =>
            new Promise<void>((resolve) => {
              const req = indexedDB.deleteDatabase(name);
              req.onsuccess = () => resolve();
              req.onerror = () => {
                logger.warn('[indexedDb] deleteDatabase failed', { name });
                resolve();
              };
              req.onblocked = () => {
                logger.warn('[indexedDb] deleteDatabase blocked', { name });
                resolve();
              };
            }),
        ),
    );
  } catch (err) {
    logger.warn('[indexedDb] deleteAllDatabases failed', { err });
  }
}
